/**
 * The access-link landing (`/access/:token`). Validates the link, then offers Sign in / Create account
 * — both carrying the link token (a valid link is required for either). On success → the game.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLogin, useRegister, useValidateLink } from "../../api/auth";
import { AuthShell, Button, FormError, TextField, apiErrorMessage } from "../../components/ui";
import logoRed from "../../assets/brand/logo-borenius-red.svg";

export function AccessPage() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const validate = useValidateLink();
  const login = useLogin();
  const register = useRegister();

  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");

  useEffect(() => {
    if (token) validate.mutate(token);
    // run once per token
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      if (mode === "login") await login.mutateAsync({ token, email, password });
      else await register.mutateAsync({ token, email, password, displayName: displayName || undefined });
      navigate("/", { replace: true });
    } catch {
      /* error rendered below */
    }
  }

  if (validate.isIdle || validate.isPending) {
    return <AuthShell>
      <p className="text-center text-ink-500">Checking your access link…</p>
    </AuthShell>;
  }

  if (!validate.data?.valid) {
    return (
      <AuthShell>
        <img src={logoRed} alt="Borenius" className="mb-6 h-6" />
        <h1 className="font-display text-fluid-xl font-semibold text-ink">This access link isn't valid</h1>
        <p className="mt-2 text-ink-600">
          It may have expired or been revoked. Please ask your administrator for a new link.
        </p>
      </AuthShell>
    );
  }

  const err = mode === "login" ? login.error : register.error;
  const busy = login.isPending || register.isPending;

  return (
    <AuthShell>
      <img src={logoRed} alt="Borenius" className="mb-6 h-6" />
      <p className="t-overline text-brand">Panic at the Closing</p>
      <h1 className="mt-1 font-display text-fluid-xl font-semibold text-ink">
        {mode === "login" ? "Welcome back, Counsel" : "Create your account"}
      </h1>
      {validate.data.label && (
        <p className="mt-1 text-sm text-ink-500">Access: {validate.data.label}</p>
      )}

      <div className="mt-5 flex gap-1 rounded-sm bg-ink-100 p-1 text-sm" role="tablist">
        {(["login", "register"] as const).map((m) => (
          <button
            key={m}
            role="tab"
            aria-selected={mode === m}
            onClick={() => setMode(m)}
            className={
              mode === m
                ? "flex-1 rounded-sm bg-white px-3 py-1.5 font-medium text-ink shadow-xs"
                : "flex-1 rounded-sm px-3 py-1.5 text-ink-500"
            }
          >
            {m === "login" ? "Sign in" : "Create account"}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="mt-5 flex flex-col gap-4">
        {mode === "register" && (
          <TextField label="Your name (optional)" name="displayName" value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoComplete="name" />
        )}
        <TextField label="Email" name="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
        <TextField
          label="Password"
          name="password"
          type="password"
          required
          minLength={mode === "register" ? 8 : undefined}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete={mode === "login" ? "current-password" : "new-password"}
        />
        {err && <FormError>{apiErrorMessage(err)}</FormError>}
        <Button type="submit" disabled={busy} className="w-full">
          {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account & play"}
        </Button>
      </form>
    </AuthShell>
  );
}
